import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import coin from "../../../assets/coin.svg";
import { ActionKind } from "../../types/Action";
import {
    FeaturedKitchenProduct,
    FeaturedProduct
} from "../../types/FeaturedProduct";
import { Button } from "../Button";
import { usePriceFormat } from "../PriceFormatProvider";
import { useRootState, RootStateKind } from "../state/StateProvider";

export const PromoPrice = ({
    discountedPrice,
    originalPrice
}: {
    discountedPrice: string;
    originalPrice: string;
}) => {
    return (
        <>
            <div className="thin pr-2 text-slate-500 line-through">
                {originalPrice}
            </div>
            <div className="justify-self-end font-semibold">
                {discountedPrice}
            </div>
        </>
    );
};

export const PromoBanner = ({
    promotionalProduct
}: {
    promotionalProduct: FeaturedProduct;
}) => {
    const { dispatch } = useRootState(RootStateKind.Scan);
    const { t } = useTranslation();
    const format = usePriceFormat();

    const [tapped, setTapped] = useState(false);

    const handleTap = () => {
        setTapped(true);
    };

    const coinAnimation = (
        <>
            {tapped && (
                <>
                    {[
                        ["-40%", "-150%"],
                        ["-20%", "-180%"],
                        ["0%", "-200%"],
                        ["20%", "-180%"],
                        ["40%", "-150%"]
                    ].map(([x, y]) => (
                        <motion.img
                            key={`${x}${y}`}
                            className="absolute z-20 pl-32 pt-5"
                            src={coin}
                            initial={{ opacity: 1 }}
                            transition={{ duration: 1.5, ease: "easeOut" }}
                            animate={{
                                opacity: [1, 1, 1, 0],
                                transform: `translateX(${x}) translateY(${y}) `
                            }}
                            onAnimationComplete={() => setTapped(false)}
                        ></motion.img>
                    ))}
                </>
            )}
        </>
    );

    const isDiscounted =
        promotionalProduct.promotionalPrice.originalPrice.value !==
        promotionalProduct.promotionalPrice.discountedPrice.value;

    return (
        <Button
            variant="subtle"
            className="relative h-full"
            onClick={() => {
                handleTap();
                dispatch({
                    kind: ActionKind.AddProduct,
                    payload: {
                        kind: "manuallyAdded",
                        code: promotionalProduct.barcode,
                        analyticsSource: "promo"
                    }
                });
            }}
        >
            {coinAnimation}
            <div
                key={promotionalProduct.name}
                className="grid shrink-0 grid-cols-4 items-center gap-3 break-words p-1 text-left text-sm"
            >
                <img
                    src={promotionalProduct.imageUrl}
                    className="max-h-8 justify-self-center bg-transparent object-contain"
                />
                <div className="col-span-3">
                    <div className="truncate font-bold capitalize">
                        {promotionalProduct.name.toLowerCase()}
                    </div>
                    {isDiscounted ? (
                        <div className="flex">
                            <PromoPrice
                                discountedPrice={format(
                                    promotionalProduct.promotionalPrice
                                        .discountedPrice
                                )}
                                originalPrice={format(
                                    promotionalProduct.promotionalPrice
                                        .originalPrice
                                )}
                            />
                        </div>
                    ) : (
                        <div className="thin font-semibold">
                            {format(
                                promotionalProduct.promotionalPrice
                                    .originalPrice
                            )}
                        </div>
                    )}
                </div>
            </div>
            {isDiscounted && (
                <div className="absolute -top-2 left-2 rounded bg-badge-background px-3 py-1 text-[18px] font-bold text-badge-foreground">
                    {t("promo")}
                </div>
            )}
        </Button>
    );
};

export const KitchenPromoBanner = ({
    product
}: {
    product: FeaturedKitchenProduct;
}) => {
    const { dispatch } = useRootState(RootStateKind.Scan);
    const format = usePriceFormat();

    return (
        <Button
            variant="subtle"
            className="relative h-full"
            data-testid={`featured-kitchen-product-${product.barcode}`}
            onClick={() => {
                dispatch({
                    kind: ActionKind.ShowKitchenMenu,
                    code: product.barcode
                });
            }}
        >
            <div
                key={product.name}
                className="grid shrink-0 grid-cols-4 items-center gap-3 break-words p-1 text-left text-sm"
            >
                <img
                    src={product.imageUrl}
                    className="max-h-8 justify-self-center bg-transparent object-contain"
                />
                <div className="col-span-3">
                    <div className="truncate font-bold capitalize">
                        {product.name.toLowerCase()}
                    </div>

                    <div className="thin font-semibold">
                        {format(product.price)}
                    </div>
                </div>
            </div>
        </Button>
    );
};
